import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { History, Search, Eye, X, Package, Calendar, FileText, Truck, Trash2, AlertTriangle, Printer, Calculator, RefreshCw, ShieldAlert, Camera, Send, CheckCircle, XCircle, ChevronDown, ChevronRight, Plus, Upload, Mail, Tag } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { printStockLabelsForGRV } from "@/utils/stockLabelPrint";
import ShelfTalkerModal from "@/components/ShelfTalkerModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const GRVHistoryModal = ({ open, onClose }) => {
  const [grvList, setGrvList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGrv, setSelectedGrv] = useState(null);
  const [viewMode, setViewMode] = useState("list"); // "list" or "detail"
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [grvToDelete, setGrvToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reconciliation, setReconciliation] = useState(null);
  const [reconciliationLoading, setReconciliationLoading] = useState(false);
  const [recalculatingReconciliation, setRecalculatingReconciliation] = useState(false);
  const [reapplyingStock, setReapplyingStock] = useState(false);

  // Claims
  const [claims, setClaims] = useState([]);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [claimItems, setClaimItems] = useState([]);
  const [claimNotes, setClaimNotes] = useState("");
  const [savingClaim, setSavingClaim] = useState(false);
  const [expandedClaim, setExpandedClaim] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);

  // Shelf talker re-print
  const [showShelfTalker, setShowShelfTalker] = useState(false);
  const [shelfTalkerItems, setShelfTalkerItems] = useState([]);

  // History fetch limit — user can extend via "Load more / Show all"
  const [historyLimit, setHistoryLimit] = useState(250);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    if (open) {
      fetchGRVHistory();
    }
  }, [open, historyLimit]);

  const fetchGRVHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/grv?limit=${historyLimit}`);
      const data = response.data || [];
      setGrvList(data);
      // If the result is exactly at the cap there are probably older GRVs.
      setHasMore(data.length >= historyLimit);
    } catch (error) {
      console.error("Error fetching GRV history:", error);
      toast.error("Failed to load GRV history");
    } finally {
      setLoading(false);
    }
  };

  const fetchGRVReconciliation = async (grvId) => {
    if (!grvId) return;
    setReconciliationLoading(true);
    try {
      const response = await axios.get(`${API}/grv/${grvId}/invoice-reconciliation`);
      setReconciliation(response.data || null);
    } catch (error) {
      console.error("Error fetching GRV reconciliation:", error);
      setReconciliation(null);
      toast.error("Failed to load invoice reconciliation");
    } finally {
      setReconciliationLoading(false);
    }
  };

  const fetchGRVDetails = async (grvId) => {
    try {
      const response = await axios.get(`${API}/grv/${grvId}`);
      setSelectedGrv(response.data);
      setViewMode("detail");
      await fetchGRVReconciliation(response.data?.id || grvId);
      fetchClaims(response.data?.id || grvId);
    } catch (error) {
      console.error("Error fetching GRV details:", error);
      toast.error("Failed to load GRV details");
    }
  };

  const handlePromoteOnceOff = async (itemIndex, item) => {
    const suggestedPlu = item.plu_code || `OO-${selectedGrv?.grv_number?.replace(/[^0-9]/g, "") || ""}-${itemIndex + 1}`;
    const plu = window.prompt(
      `Convert "${item.name}" to Stock Master?\n\n` +
      `Cost: ${formatCurrency(item.unit_cost)} · Qty received: ${item.quantity_received}\n\n` +
      `Enter the PLU code for the new product (must be unique):`,
      suggestedPlu
    );
    if (!plu) return;
    const markupStr = window.prompt("Markup % to use for selling price?\n(applied as cost × (1 + markup/100), then VAT added)", "30");
    if (markupStr === null) return;
    const markup = parseFloat(markupStr) || 30;

    try {
      const res = await axios.post(
        `${API}/grv/${selectedGrv.id}/convert-once-off-to-product`,
        { item_index: itemIndex, plu_code: plu.trim().toUpperCase(), markup_percent: markup }
      );
      const p = res.data?.product || {};
      toast.success(
        `Created PLU ${p.plu_code} — stock ${p.current_stock}, selling at ${formatCurrency(p.selling_price_incl_vat)}`
      );
      await fetchGRVHistory();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to convert once-off item");
    }
  };

  const handleReverseDoubledStock = async () => {
    if (!selectedGrv?.id) return;
    if (!window.confirm(`Fix doubled stock for ${selectedGrv.grv_number}?\n\nThis SUBTRACTS each line's received quantity ONCE from current stock. Use only if this GRV's stock was added twice.\n\nThis can only be done once per GRV.`)) return;
    try {
      const { data } = await axios.post(`${API}/grv/${selectedGrv.id}/reverse-duplicate-stock`);
      const lines = (data.applied || []).map((a) => `• ${a.plu_code || ""} ${a.name}: -${a.qty_removed} → now ${a.new_stock}`);
      window.alert([`Fixed ${data.applied_count} item(s) (${data.adjustment_number}):`, ...lines].join("\n"));
      toast.success(`Doubled stock fixed for ${data.grv_number}`);
      await fetchGRVHistory();
      setSelectedGrv((g) => (g ? { ...g, duplicate_stock_reversed_at: new Date().toISOString() } : g));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fix doubled stock");
    }
  };

  const handleReapplyStock = async () => {
    if (!selectedGrv?.id) return;
    const alreadyApplied = selectedGrv?.stock_applied === true;
    const confirmMsg = alreadyApplied
      ? `This GRV is already marked as having stock applied.\n\nForce re-apply? Doing this will ADD the GRV quantities to current stock again — DOUBLE-COUNT IS POSSIBLE if stock was already added.\n\nOnly continue if you've verified stock didn't update for these products.`
      : `Re-apply stock from ${selectedGrv.grv_number}? This will add each item's received quantity to the matching product's current_stock.\n\nUse only if stock didn't update on the original GRV submission.`;
    if (!window.confirm(confirmMsg)) return;

    setReapplyingStock(true);
    try {
      const url = `${API}/grv/${selectedGrv.id}/reapply-stock${alreadyApplied ? "?force=true" : ""}`;
      const response = await axios.post(url);
      const data = response.data || {};
      const lines = (data.applied || []).map(
        (a) => `• ${a.plu_code || ""} ${a.name}: ${a.previous_stock} → ${a.new_stock} (+${a.qty_received})`
      );
      const skippedLines = (data.skipped || []).map((s) => `• ${s.name || s.product_id}: ${s.reason}`);
      const summary = [
        `Re-applied stock on ${data.applied_count} item(s):`,
        ...lines,
        ...(data.skipped_count ? ["", `Skipped ${data.skipped_count}:`, ...skippedLines] : []),
      ].join("\n");
      window.alert(summary);
      toast.success(`Re-applied stock for ${data.grv_number}`);
      await fetchGRVHistory();
    } catch (error) {
      console.error("Error re-applying stock:", error);
      toast.error(error.response?.data?.detail || "Failed to re-apply stock");
    } finally {
      setReapplyingStock(false);
    }
  };

  const handleRecalculateReconciliation = async () => {
    if (!selectedGrv?.id) return;

    setRecalculatingReconciliation(true);
    try {
      const response = await axios.post(`${API}/grv/${selectedGrv.id}/recalculate-reconciliation`);
      const payload = response.data || {};
      setSelectedGrv(payload.updated_grv || selectedGrv);
      setReconciliation(payload.reconciliation || null);
      await fetchGRVHistory();

      if (payload.updated) {
        toast.success(`Recalculated ${payload.grv_number}: totals were updated.`);
      } else {
        toast.success(`No recalculation changes needed for ${payload.grv_number}.`);
      }
    } catch (error) {
      console.error("Error recalculating GRV reconciliation:", error);
      toast.error(error.response?.data?.detail || "Failed to recalculate GRV");
    } finally {
      setRecalculatingReconciliation(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-ZA", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const formatCurrency = (amount) => {
    return `R ${(amount || 0).toFixed(2)}`;
  };

  const parseBooleanLike = (value, defaultValue = false) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value !== 0;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (["true", "1", "yes", "y", "on"].includes(normalized)) return true;
      if (["false", "0", "no", "n", "off", ""].includes(normalized)) return false;
    }
    return defaultValue;
  };

  const resolveIncludeDeliveryInTotals = (grv, reconciliationPayload = null) => {
    const fromReconciliation = reconciliationPayload?.calculated?.delivery_fee_included_in_totals;
    if (typeof fromReconciliation === "boolean") return fromReconciliation;

    const directFlag = grv?.include_delivery_fee_in_totals;
    if (directFlag !== undefined) return parseBooleanLike(directFlag, false);

    const legacyFlag = grv?.include_delivery_fee_in_total;
    if (legacyFlag !== undefined) return parseBooleanLike(legacyFlag, false);

    return false;
  };

  const filteredGrvs = grvList.filter(grv => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      grv.grv_number?.toLowerCase().includes(query) ||
      grv.supplier_name?.toLowerCase().includes(query) ||
      grv.supplier_invoice_number?.toLowerCase().includes(query) ||
      grv.po_number?.toLowerCase().includes(query)
    );
  });

  const handleBack = () => {
    setSelectedGrv(null);
    setReconciliation(null);
    setViewMode("list");
  };

  const handlePrintGrv = (grv) => {
    if (!grv) return;
    const rows = (grv.items || []).map((item, i) => `
      <tr>
        <td style="padding:4px 8px;border-bottom:1px solid #eee;text-align:center">${i + 1}</td>
        <td style="padding:4px 8px;border-bottom:1px solid #eee">${item.name}<br/><span style="font-size:10px;color:#888">PLU: ${item.plu_code || ''} | Barcode: ${item.barcode || 'N/A'}</span></td>
        <td style="padding:4px 8px;border-bottom:1px solid #eee;text-align:center">${item.quantity_received}</td>
        <td style="padding:4px 8px;border-bottom:1px solid #eee;text-align:right">${formatCurrency(item.unit_cost)}</td>
        <td style="padding:4px 8px;border-bottom:1px solid #eee;text-align:right">${item.new_selling_price ? formatCurrency(item.new_selling_price) : '-'}</td>
        <td style="padding:4px 8px;border-bottom:1px solid #eee;text-align:right;font-weight:600">${formatCurrency(item.total_cost)}</td>
      </tr>`).join('');

    const includeDeliveryInTotals = resolveIncludeDeliveryInTotals(grv);
    const deliveryFeeExclVat = grv.delivery_fee_excl_vat || 0;
    const deliveryFeeVat = (grv.delivery_fee_vat ?? (deliveryFeeExclVat > 0 ? deliveryFeeExclVat * 0.15 : 0)) || 0;
    const itemsSubtotal = includeDeliveryInTotals ? ((grv.subtotal || 0) - deliveryFeeExclVat) : (grv.subtotal || 0);
    const vatOnItems = includeDeliveryInTotals ? Math.max(0, (grv.vat_amount || 0) - deliveryFeeVat) : (grv.vat_amount || 0);
    const invoiceType = (grv.invoice_amount_type || "excl").toLowerCase();
    const comparisonBasisLabel = invoiceType === "incl" ? "Total (Incl VAT)" : "Subtotal (Excl VAT)";
    const calculatedComparisonAmount = invoiceType === "incl" ? (grv.total || 0) : (grv.subtotal || 0);
    const variance = calculatedComparisonAmount - (grv.invoice_amount || 0);
    const isBalanced = Math.abs(variance) <= 0.01;

    const html = `<!DOCTYPE html><html><head><title>GRV ${grv.grv_number}</title>
      <style>
        body{font-family:Arial,sans-serif;margin:20px;font-size:12px}
        h1{font-size:18px;margin:0 0 4px}
        table{width:100%;border-collapse:collapse;margin-top:12px}
        th{background:#f3f4f6;padding:6px 8px;text-align:left;font-size:11px;border-bottom:2px solid #d1d5db}
        .info-grid{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:8px;margin-bottom:8px}
        .info-box{border:1px solid #e5e7eb;padding:6px 8px;border-radius:4px}
        .info-label{font-size:9px;color:#6b7280;text-transform:uppercase}
        .info-value{font-weight:600;font-size:13px}
        .totals{text-align:right;margin-top:12px}
        .totals td{padding:3px 8px}
        .total-row{font-size:16px;font-weight:bold;border-top:2px solid #111}
        @media print{body{margin:10px}}
      </style></head><body>
      <h1>GOODS RECEIVED VOUCHER</h1>
      <div class="info-grid">
        <div class="info-box"><div class="info-label">GRV Number</div><div class="info-value" style="color:#2563eb">${grv.grv_number}</div></div>
        <div class="info-box"><div class="info-label">Date</div><div class="info-value">${formatDate(grv.received_date)}</div></div>
        <div class="info-box"><div class="info-label">Supplier</div><div class="info-value">${grv.supplier_name || '-'}</div></div>
        <div class="info-box"><div class="info-label">Invoice #</div><div class="info-value">${grv.supplier_invoice_number || '-'}</div></div>
      </div>
      ${grv.po_number ? `<div style="margin-bottom:8px;font-size:11px">PO Reference: <strong>${grv.po_number}</strong></div>` : ''}
      <table>
        <thead><tr>
          <th style="width:30px;text-align:center">#</th><th>Product</th><th style="text-align:center">Qty</th>
          <th style="text-align:right">Unit Cost</th><th style="text-align:right">New Price</th><th style="text-align:right">Total</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <table class="totals" style="width:340px;margin-left:auto">
        <tr><td style="color:#6b7280">Items Subtotal (Excl VAT):</td><td>${formatCurrency(itemsSubtotal)}</td></tr>
        ${deliveryFeeExclVat > 0 ? `<tr><td style="color:#6b7280">Delivery Fee (Excl VAT)${includeDeliveryInTotals ? '' : ' - excluded from total'}:</td><td>${formatCurrency(deliveryFeeExclVat)}</td></tr>` : ''}
        <tr><td style="color:#6b7280">Subtotal (Excl VAT):</td><td>${formatCurrency(grv.subtotal)}</td></tr>
        <tr><td style="color:#6b7280">VAT on Items:</td><td>${formatCurrency(vatOnItems)}</td></tr>
        ${deliveryFeeExclVat > 0 ? `<tr><td style="color:#6b7280">VAT on Delivery${includeDeliveryInTotals ? '' : ' (excluded)'}:</td><td>${formatCurrency(deliveryFeeVat)}</td></tr>` : ''}
        <tr><td style="color:#6b7280">VAT Total:</td><td>${formatCurrency(grv.vat_amount)}</td></tr>
        <tr class="total-row"><td>Total:</td><td style="color:#2563eb">${formatCurrency(grv.total)}</td></tr>
      </table>
      <div style="margin-top:12px;border:1px solid #dbeafe;border-radius:6px;padding:10px;background:#f8fbff">
        <div style="font-weight:700;color:#1e3a8a;margin-bottom:6px">Invoice Reconciliation</div>
        <table style="width:100%;font-size:11px">
          <tr><td style="color:#6b7280">Supplier Invoice (${invoiceType.toUpperCase()}):</td><td style="text-align:right">${formatCurrency(grv.invoice_amount || 0)}</td></tr>
          <tr><td style="color:#6b7280">Comparison Basis:</td><td style="text-align:right">${comparisonBasisLabel}</td></tr>
          <tr><td style="color:#6b7280">Calculated (${comparisonBasisLabel}):</td><td style="text-align:right">${formatCurrency(calculatedComparisonAmount)}</td></tr>
          <tr><td style="color:#6b7280">Variance:</td><td style="text-align:right;font-weight:700;color:${isBalanced ? '#166534' : '#b91c1c'}">${formatCurrency(variance)}</td></tr>
          <tr><td style="color:#6b7280">Status:</td><td style="text-align:right;font-weight:700;color:${isBalanced ? '#166534' : '#b91c1c'}">${isBalanced ? 'BALANCED' : 'OUT OF BALANCE'}</td></tr>
        </table>
      </div>
      ${grv.notes ? `<div style="margin-top:16px;padding:8px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:4px;font-size:11px"><strong>Notes:</strong> ${grv.notes}</div>` : ''}
      </body></html>`;

    const w = window.open('', '_blank', 'width=800,height=600');
    w.document.write(html);
    w.document.close();
    w.onload = () => { w.print(); };
  };

  // Open the Shelf Talker modal pre-loaded with ALL items from this GRV.
  // Always fetches the full GRV first (list endpoint may return shallow items),
  // and fills missing prices from the current Stock Master selling price.
  const handleReprintShelfTalkers = async (grv) => {
    if (!grv?.id) return;
    try {
      let fullGrv = grv;
      if (!grv.items || grv.items.length === 0) {
        const res = await axios.get(`${API}/grv/${grv.id}`);
        fullGrv = res.data || grv;
      }
      const rawItems = (fullGrv.items || [])
        .filter(it => Number(it.quantity_received || 0) > 0);
      if (rawItems.length === 0) {
        toast.warning("No items on this GRV to print shelf talkers for.");
        return;
      }

      // Resolve a price + barcode for each item: prefer what's on the GRV,
      // otherwise fall back to the current Stock Master record.
      const items = await Promise.all(rawItems.map(async (it) => {
        let price = Number(it.new_selling_price ?? it.selling_price ?? 0);
        let barcode = it.barcode ? String(it.barcode).trim() : "";
        if ((!price || !barcode) && it.product_id) {
          try {
            const p = await axios.get(`${API}/products/${it.product_id}`);
            if (!price) price = Number(p.data?.selling_price_incl_vat || 0);
            if (!barcode) barcode = p.data?.barcode ? String(p.data.barcode).trim() : "";
          } catch (_) { /* leave defaults */ }
        }
        return {
          product_id: it.product_id,
          plu_code: it.plu_code,
          barcode,
          name: it.name || it.product_name || "",
          selling_price: price,
          shelf_number: it.shelf_number,
          bin_number: it.bin_number,
          location: it.location,
          // Pre-select every item for re-print regardless of price-change flag.
          print_shelf_talker: true,
        };
      }));

      setShelfTalkerItems(items);
      setShowShelfTalker(true);
    } catch (err) {
      console.error("Reprint shelf talkers failed:", err);
      toast.error(err.response?.data?.detail || "Failed to load GRV for reprint");
    }
  };

  // --- Claims functions ---
  const fetchClaims = async (grvId) => {
    setClaimsLoading(true);
    try {
      const res = await axios.get(`${API}/grv-claims?grv_id=${grvId}`);
      setClaims(res.data || []);
    } catch (e) { console.error("Error fetching claims:", e); }
    finally { setClaimsLoading(false); }
  };

  const openClaimForm = () => {
    if (!selectedGrv?.items?.length) return;
    setClaimItems(selectedGrv.items.map(item => ({
      plu_code: item.plu_code || "",
      product_name: item.name || "",
      product_id: item.product_id || "",
      quantity_received: item.quantity_received || 0,
      unit_cost: item.unit_cost || 0,
      claim_type: "",
      claim_qty: 0,
      reason: "",
      selected: false,
    })));
    setClaimNotes("");
    setShowClaimForm(true);
  };

  const handleSaveClaim = async () => {
    const selected = claimItems.filter(i => i.selected && i.claim_qty > 0 && i.claim_type);
    if (selected.length === 0) {
      toast.error("Select at least one item with quantity and claim type");
      return;
    }
    setSavingClaim(true);
    try {
      const payload = {
        grv_id: selectedGrv.id,
        items: selected.map(i => ({
          plu_code: i.plu_code,
          product_name: i.product_name,
          product_id: i.product_id,
          claim_type: i.claim_type,
          quantity: i.claim_qty,
          unit_cost: i.unit_cost,
          reason: i.reason,
        })),
        notes: claimNotes,
      };
      const res = await axios.post(`${API}/grv-claims`, payload);
      toast.success(`Claim ${res.data.claim_number} created`);
      setShowClaimForm(false);
      fetchClaims(selectedGrv.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create claim");
    } finally { setSavingClaim(false); }
  };

  const updateClaimStatus = async (claimId, status) => {
    try {
      await axios.put(`${API}/grv-claims/${claimId}/status`, { status });
      toast.success(`Claim status updated to ${status}`);
      fetchClaims(selectedGrv.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    }
  };

  const handlePhotoUpload = async (claimId, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(claimId);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await axios.post(`${API}/grv-claims/${claimId}/photos`, { image: reader.result });
        toast.success("Photo uploaded");
        fetchClaims(selectedGrv.id);
      } catch (e) {
        toast.error("Photo upload failed");
      } finally { setUploadingPhoto(null); }
    };
    reader.readAsDataURL(file);
  };

  const handlePrintClaim = (claim) => {
    const rows = claim.items.map((item, i) =>
      `<tr><td>${i+1}</td><td>${item.product_name}<br/><span style="font-size:10px;color:#888">PLU: ${item.plu_code}</span></td><td>${item.claim_type.replace("_"," ")}</td><td style="text-align:center">${item.quantity}</td><td style="text-align:right">R ${item.unit_cost.toFixed(2)}</td><td style="text-align:right">R ${item.claim_value.toFixed(2)}</td></tr>`
    ).join("");
    const photos = (claim.photos || []).map(p =>
      p.url.startsWith("data:") ? `<img src="${p.url}" style="max-width:200px;max-height:200px;margin:4px;border:1px solid #ddd;border-radius:4px"/>` : `<img src="${p.url}" style="max-width:200px;max-height:200px;margin:4px;border:1px solid #ddd;border-radius:4px"/>`
    ).join("");
    const html = `<!DOCTYPE html><html><head><title>Claim ${claim.claim_number}</title><style>body{font-family:Arial,sans-serif;padding:20px;font-size:12px}h1{font-size:18px;margin-bottom:4px}table{width:100%;border-collapse:collapse;margin:12px 0}th,td{border:1px solid #ddd;padding:6px 8px;text-align:left}th{background:#f3f4f6;font-size:11px}
    .info{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin:12px 0;font-size:12px}.info div{padding:6px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:4px}.info label{font-size:10px;color:#6b7280;text-transform:uppercase;display:block}.total{text-align:right;font-size:16px;font-weight:bold;margin-top:8px;color:#dc2626}
    </style></head><body>
    <h1>SUPPLIER CLAIM: ${claim.claim_number}</h1>
    <div class="info">
      <div><label>GRV Reference</label>${claim.grv_number}</div>
      <div><label>Date</label>${claim.created_at?.split("T")[0]}</div>
      <div><label>Supplier</label>${claim.supplier_name}</div>
      <div><label>Invoice #</label>${claim.supplier_invoice_number || "-"}</div>
      <div><label>Status</label>${claim.status}</div>
      <div><label>Total Claim Value</label>R ${claim.total_claim_value.toFixed(2)}</div>
    </div>
    <table><thead><tr><th>#</th><th>Product</th><th>Claim Type</th><th style="text-align:center">Qty</th><th style="text-align:right">Unit Cost</th><th style="text-align:right">Value</th></tr></thead><tbody>${rows}</tbody></table>
    <div class="total">Total Claim: R ${claim.total_claim_value.toFixed(2)}</div>
    ${claim.notes ? `<div style="margin-top:12px;padding:8px;background:#fef3c7;border:1px solid #fcd34d;border-radius:4px"><strong>Notes:</strong> ${claim.notes}</div>` : ""}
    ${photos ? `<div style="margin-top:12px"><strong>Photo Evidence:</strong><br/>${photos}</div>` : ""}
    </body></html>`;
    const w = window.open("", "_blank", "width=800,height=600");
    w.document.write(html);
    w.document.close();
    w.onload = () => { w.print(); };
  };

  const handleEmailClaim = async (claim) => {
    let defaultEmail = "";
    if (claim.supplier_id) {
      try {
        const supRes = await axios.get(`${API}/suppliers`);
        const supplier = (supRes.data || []).find(s => s.id === claim.supplier_id);
        if (supplier?.email) defaultEmail = supplier.email;
      } catch { /* ignore */ }
    }

    const email = window.prompt(
      `Email claim ${claim.claim_number} to supplier.\n\nEnter supplier email address:`,
      defaultEmail
    );
    if (!email) return;

    try {
      const res = await axios.post(`${API}/grv-claims/${claim.id}/email`, {
        recipient_email: email,
      });
      if (res.data?.status === "blocked") {
        toast.warning(res.data.message || "Emails are disabled in settings");
      } else {
        toast.success(`Claim emailed to ${email}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to email claim");
    }
  };

  const handleDeleteClick = (grv) => {
    setGrvToDelete(grv);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!grvToDelete) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/grv/${grvToDelete.id}?reason=Manual reversal from GRV History`);
      
      toast.success(
        <div>
          <div className="font-semibold">GRV {grvToDelete.grv_number} Reversed & Deleted</div>
          <div className="text-sm">
            {response.data.items_reversed} items reversed, stock updated
          </div>
        </div>,
        { duration: 5000 }
      );
      
      // Refresh the list
      fetchGRVHistory();
      
      // If we were viewing this GRV, go back to list
      if (selectedGrv?.id === grvToDelete.id) {
        setSelectedGrv(null);
        setViewMode("list");
      }
    } catch (error) {
      console.error("Error deleting GRV:", error);
      toast.error(error.response?.data?.detail || "Failed to delete GRV");
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setGrvToDelete(null);
    }
  };

  const includeDeliveryInTotalsForDetail = resolveIncludeDeliveryInTotals(selectedGrv, reconciliation);
  const historyCalculationAudit = reconciliation ? {
    itemsSubtotalExclVat: Number(reconciliation?.calculated?.items_subtotal_excl_vat || 0),
    itemsVat: Number(reconciliation?.calculated?.vat_on_items || 0),
    deliveryFeeExclVat: Number(reconciliation?.calculated?.delivery_fee_excl_vat || 0),
    deliveryFeeVat: Number(reconciliation?.calculated?.vat_on_delivery || 0),
    subtotalExclVat: Number(reconciliation?.calculated?.subtotal_excl_vat || 0),
    vatTotal: Number(reconciliation?.calculated?.vat_total || 0),
    totalInclVat: Number(reconciliation?.calculated?.total_incl_vat || 0),
    invoiceAmount: Number(reconciliation?.invoice?.amount || 0),
    comparisonAmount: Number(reconciliation?.calculated_comparison_amount || 0),
    variance: Number(reconciliation?.variance || 0),
    isBalanced: Boolean(reconciliation?.is_balanced),
    subtotalCheckPass: Math.abs((Number(reconciliation?.calculated?.items_subtotal_excl_vat || 0) + (includeDeliveryInTotalsForDetail ? Number(reconciliation?.calculated?.delivery_fee_excl_vat || 0) : 0)) - Number(reconciliation?.calculated?.subtotal_excl_vat || 0)) <= 0.01,
    vatCheckPass: Math.abs((Number(reconciliation?.calculated?.vat_on_items || 0) + (includeDeliveryInTotalsForDetail ? Number(reconciliation?.calculated?.vat_on_delivery || 0) : 0)) - Number(reconciliation?.calculated?.vat_total || 0)) <= 0.01,
    totalCheckPass: Math.abs((Number(reconciliation?.calculated?.subtotal_excl_vat || 0) + Number(reconciliation?.calculated?.vat_total || 0)) - Number(reconciliation?.calculated?.total_incl_vat || 0)) <= 0.01,
  } : null;

  return (
    <>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-blue-600" />
            {viewMode === "list" ? "GRV History" : `GRV Details - ${selectedGrv?.grv_number}`}
          </DialogTitle>
        </DialogHeader>

        {viewMode === "list" ? (
          /* LIST VIEW */
          <div className="flex-1 overflow-auto">
            {/* Search + History depth */}
            <div className="mb-4 flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[260px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by GRV #, Supplier, Invoice #, or PO #..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <span>Showing last</span>
                <select
                  value={historyLimit}
                  onChange={(e) => setHistoryLimit(Number(e.target.value))}
                  className="h-9 border rounded px-2 text-sm bg-white"
                  data-testid="grv-history-limit-select"
                >
                  <option value={100}>100 GRVs</option>
                  <option value={250}>250 GRVs</option>
                  <option value={500}>500 GRVs</option>
                  <option value={1000}>1 000 GRVs</option>
                  <option value={5000}>All (5 000)</option>
                </select>
                <span className="text-gray-400">·</span>
                <span>{grvList.length} loaded</span>
                {hasMore && (
                  <span className="text-amber-600 font-medium">(may have older — increase limit)</span>
                )}
              </div>
            </div>

            {/* GRV List */}
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading GRV history...</div>
            ) : filteredGrvs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No GRVs found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>GRV #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>PO #</TableHead>
                    <TableHead className="text-right">Items</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredGrvs.map((grv) => (
                    <TableRow key={grv.id} className="hover:bg-gray-50">
                      <TableCell className="font-mono font-medium text-blue-600">
                        {grv.grv_number}
                      </TableCell>
                      <TableCell>{formatDate(grv.received_date || grv.created_at)}</TableCell>
                      <TableCell>{grv.supplier_name || "-"}</TableCell>
                      <TableCell>{grv.supplier_invoice_number || "-"}</TableCell>
                      <TableCell>{grv.po_number || "-"}</TableCell>
                      <TableCell className="text-right">{grv.items?.length || 0}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(grv.total)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              fetchGRVDetails(grv.id);
                            }}
                            className="text-blue-600 hover:text-blue-800"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReprintShelfTalkers(grv);
                            }}
                            className="text-amber-700 hover:text-amber-900 hover:bg-amber-50"
                            title="Re-print Shelf Talkers"
                            data-testid={`grv-reprint-shelf-talkers-${grv.grv_number}`}
                          >
                            <Tag className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleDeleteClick(grv);
                            }}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50"
                            title="Delete & Reverse GRV"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        ) : (
          /* DETAIL VIEW */
          <div className="flex-1 overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <Button variant="outline" size="sm" onClick={handleBack}>
                <X className="h-4 w-4 mr-2" /> Back to List
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRecalculateReconciliation()}
                  disabled={recalculatingReconciliation}
                  data-testid="grv-recalculate-reconciliation-button"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${recalculatingReconciliation ? "animate-spin" : ""}`} />
                  {recalculatingReconciliation ? "Recalculating..." : "Recalculate Totals"}
                </Button>
                {selectedGrv?.stock_applied === true && !selectedGrv?.duplicate_stock_reversed_at && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleReverseDoubledStock}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    data-testid="grv-fix-doubled-stock-btn"
                    title="Subtract this GRV's quantities once, only if its stock was added twice"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" /> Fix Doubled Stock
                  </Button>
                )}
                {selectedGrv?.stock_applied !== true && <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleReapplyStock()}
                  disabled={reapplyingStock}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="grv-reapply-stock-btn"
                  title="Re-add stock from this GRV (use only if stock didn't update)"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${reapplyingStock ? "animate-spin" : ""}`} />
                  {reapplyingStock ? "Applying..." : "Re-apply Stock"}
                </Button>}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePrintGrv(selectedGrv)}
                  data-testid="print-grv-btn"
                >
                  <Printer className="h-4 w-4 mr-2" /> Print GRV
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => printStockLabelsForGRV(selectedGrv, toast)}
                  className="text-amber-700 border-amber-300 hover:bg-amber-50"
                  data-testid="print-stock-labels-history-btn"
                >
                  <Printer className="h-4 w-4 mr-2" /> Print Stock Labels
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={() => handleDeleteClick(selectedGrv)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Delete & Reverse GRV
                </Button>
              </div>
            </div>

            {selectedGrv && (
              <div className="space-y-4">
                {/* Header Info */}
                <Card>
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <div className="text-xs text-gray-500 uppercase">GRV Number</div>
                        <div className="font-mono font-bold text-lg text-blue-600">
                          {selectedGrv.grv_number}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase">Received Date</div>
                        <div className="font-medium flex items-center gap-1">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          {formatDate(selectedGrv.received_date)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase">Supplier</div>
                        <div className="font-medium flex items-center gap-1">
                          <Truck className="h-4 w-4 text-gray-400" />
                          {selectedGrv.supplier_name || "-"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase">Invoice #</div>
                        <div className="font-medium">{selectedGrv.supplier_invoice_number || "-"}</div>
                      </div>
                    </div>
                    {selectedGrv.po_number && (
                      <div className="mt-3 pt-3 border-t">
                        <span className="text-xs text-gray-500">PO Reference: </span>
                        <span className="font-mono">{selectedGrv.po_number}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Items Table */}
                <Card>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="w-[60px]">#</TableHead>
                          <TableHead>Product</TableHead>
                          <TableHead className="text-right">Qty Received</TableHead>
                          <TableHead className="text-right">Unit Cost</TableHead>
                          <TableHead className="text-right">New Price</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedGrv.items?.map((item, index) => {
                          const isOnceOff = item.is_once_off === true || String(item.product_id || "").startsWith("once-off-");
                          return (
                          <TableRow key={index} className={isOnceOff ? "bg-amber-50" : ""}>
                            <TableCell className="font-mono text-sm">{index + 1}</TableCell>
                            <TableCell>
                              <div className="font-medium">
                                {item.name}
                                {isOnceOff && (
                                  <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">once-off</span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500">
                                PLU: {item.plu_code || "—"} | Barcode: {item.barcode || "N/A"}
                              </div>
                              {isOnceOff && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="mt-1 h-7 px-2 text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                                  onClick={() => handlePromoteOnceOff(index, item)}
                                  data-testid={`grv-promote-once-off-${index}`}
                                >
                                  Convert to Stock Master
                                </Button>
                              )}
                            </TableCell>
                            <TableCell className="text-right">{item.quantity_received}</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.unit_cost)}</TableCell>
                            <TableCell className="text-right">
                              {item.new_selling_price ? formatCurrency(item.new_selling_price) : "-"}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(item.total_cost)}
                            </TableCell>
                          </TableRow>
                        )})}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Totals */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex justify-end">
                      <div className="w-64 space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Items Subtotal (Excl VAT):</span>
                          <span className="font-medium">{formatCurrency(includeDeliveryInTotalsForDetail ? ((selectedGrv.subtotal || 0) - (selectedGrv.delivery_fee_excl_vat || 0)) : (selectedGrv.subtotal || 0))}</span>
                        </div>
                        {(selectedGrv.delivery_fee_excl_vat || 0) > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Delivery Fee (Excl VAT){includeDeliveryInTotalsForDetail ? "" : " - excluded from total"}:</span>
                            <span className="font-medium">{formatCurrency(selectedGrv.delivery_fee_excl_vat)}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-500">Subtotal (Excl VAT):</span>
                          <span className="font-medium">{formatCurrency(selectedGrv.subtotal)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">VAT ({includeDeliveryInTotalsForDetail ? "on applicable items + delivery" : "on applicable items only"}):</span>
                          <span className="font-medium">{formatCurrency(selectedGrv.vat_amount)}</span>
                        </div>
                        <div className="flex justify-between text-lg font-bold border-t pt-2">
                          <span>Total:</span>
                          <span className="text-blue-600">{formatCurrency(selectedGrv.total)}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Invoice Reconciliation */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Calculator className="h-4 w-4 text-blue-600" />
                        <div className="text-sm font-semibold text-blue-900" data-testid="grv-reconciliation-heading">Invoice Reconciliation</div>
                      </div>
                      {reconciliation && (
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${reconciliation.is_balanced ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}
                          data-testid="grv-reconciliation-status-badge"
                        >
                          {reconciliation.status}
                        </span>
                      )}
                    </div>

                    {reconciliationLoading ? (
                      <div className="text-sm text-gray-500" data-testid="grv-reconciliation-loading">Loading reconciliation...</div>
                    ) : !reconciliation ? (
                      <div className="text-sm text-red-600" data-testid="grv-reconciliation-empty">Unable to load reconciliation data.</div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="space-y-2 border rounded-lg p-3">
                          <div className="flex justify-between" data-testid="grv-recon-invoice-type-row">
                            <span className="text-gray-500">Invoice Type:</span>
                            <span className="font-medium uppercase" data-testid="grv-recon-invoice-type">{reconciliation.invoice?.amount_type}</span>
                          </div>
                          <div className="flex justify-between" data-testid="grv-recon-invoice-amount-row">
                            <span className="text-gray-500">Invoice Amount:</span>
                            <span className="font-medium" data-testid="grv-recon-invoice-amount">{formatCurrency(reconciliation.invoice?.amount)}</span>
                          </div>
                          <div className="flex justify-between" data-testid="grv-recon-comparison-basis-row">
                            <span className="text-gray-500">Comparison Basis:</span>
                            <span className="font-medium" data-testid="grv-recon-comparison-basis">{reconciliation.comparison_basis === "total_incl_vat" ? "Total (Incl VAT)" : "Subtotal (Excl VAT)"}</span>
                          </div>
                          <div className="flex justify-between" data-testid="grv-recon-calculated-amount-row">
                            <span className="text-gray-500">Calculated Amount:</span>
                            <span className="font-medium" data-testid="grv-recon-calculated-amount">{formatCurrency(reconciliation.calculated_comparison_amount)}</span>
                          </div>
                          <div className="flex justify-between border-t pt-2" data-testid="grv-recon-variance-row">
                            <span className="text-gray-700 font-medium">Variance:</span>
                            <span className={`font-bold ${reconciliation.is_balanced ? "text-emerald-700" : "text-red-700"}`} data-testid="grv-recon-variance-amount">{formatCurrency(reconciliation.variance)}</span>
                          </div>
                          <div className="text-xs text-gray-500" data-testid="grv-recon-threshold">Balanced threshold: ±R{(reconciliation.tolerance ?? 0.01).toFixed(2)}</div>
                        </div>

                        <div className="space-y-2 border rounded-lg p-3">
                          <div className="flex justify-between" data-testid="grv-recon-items-subtotal-row">
                            <span className="text-gray-500">Items Subtotal (Excl VAT):</span>
                            <span className="font-medium" data-testid="grv-recon-items-subtotal">{formatCurrency(reconciliation.calculated?.items_subtotal_excl_vat)}</span>
                          </div>
                          <div className="flex justify-between" data-testid="grv-recon-delivery-subtotal-row">
                            <span className="text-gray-500">Delivery Fee (Excl VAT){reconciliation.calculated?.delivery_fee_included_in_totals ? "" : " - excluded from totals"}:</span>
                            <span className="font-medium" data-testid="grv-recon-delivery-subtotal">{formatCurrency(reconciliation.calculated?.delivery_fee_excl_vat)}</span>
                          </div>
                          <div className="flex justify-between" data-testid="grv-recon-vat-items-row">
                            <span className="text-gray-500">VAT on Items:</span>
                            <span className="font-medium" data-testid="grv-recon-vat-items">{formatCurrency(reconciliation.calculated?.vat_on_items)}</span>
                          </div>
                          <div className="flex justify-between" data-testid="grv-recon-vat-delivery-row">
                            <span className="text-gray-500">VAT on Delivery{reconciliation.calculated?.delivery_fee_included_in_totals ? "" : " (excluded)"}:</span>
                            <span className="font-medium" data-testid="grv-recon-vat-delivery">{formatCurrency(reconciliation.calculated?.vat_on_delivery)}</span>
                          </div>
                          <div className="flex justify-between" data-testid="grv-recon-subtotal-row">
                            <span className="text-gray-500">Subtotal (Excl VAT):</span>
                            <span className="font-medium" data-testid="grv-recon-subtotal">{formatCurrency(reconciliation.calculated?.subtotal_excl_vat)}</span>
                          </div>
                          <div className="flex justify-between" data-testid="grv-recon-vat-total-row">
                            <span className="text-gray-500">VAT Total:</span>
                            <span className="font-medium" data-testid="grv-recon-vat-total">{formatCurrency(reconciliation.calculated?.vat_total)}</span>
                          </div>
                          <div className="flex justify-between border-t pt-2" data-testid="grv-recon-total-row">
                            <span className="text-gray-700 font-medium">Total (Incl VAT):</span>
                            <span className="font-bold text-blue-700" data-testid="grv-recon-total">{formatCurrency(reconciliation.calculated?.total_incl_vat)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {historyCalculationAudit && (
                  <Card data-testid="grv-history-calculation-audit-panel">
                    <CardContent className="p-4 space-y-3">
                      <div className="text-sm font-semibold text-sky-900" data-testid="grv-history-calculation-audit-heading">
                        GRV Calculation Audit
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div className="bg-sky-50 border border-sky-200 rounded p-2" data-testid="grv-history-audit-items-subtotal-card">
                          <div className="text-gray-500">Items Subtotal</div>
                          <div className="font-semibold" data-testid="grv-history-audit-items-subtotal">{formatCurrency(historyCalculationAudit.itemsSubtotalExclVat)}</div>
                        </div>
                        <div className="bg-sky-50 border border-sky-200 rounded p-2" data-testid="grv-history-audit-items-vat-card">
                          <div className="text-gray-500">VAT on Items</div>
                          <div className="font-semibold" data-testid="grv-history-audit-items-vat">{formatCurrency(historyCalculationAudit.itemsVat)}</div>
                        </div>
                        <div className="bg-sky-50 border border-sky-200 rounded p-2" data-testid="grv-history-audit-delivery-subtotal-card">
                          <div className="text-gray-500">Delivery Excl VAT</div>
                          <div className="font-semibold" data-testid="grv-history-audit-delivery-subtotal">{formatCurrency(historyCalculationAudit.deliveryFeeExclVat)}</div>
                        </div>
                        <div className="bg-sky-50 border border-sky-200 rounded p-2" data-testid="grv-history-audit-delivery-vat-card">
                          <div className="text-gray-500">Delivery VAT</div>
                          <div className="font-semibold" data-testid="grv-history-audit-delivery-vat">{formatCurrency(historyCalculationAudit.deliveryFeeVat)}</div>
                        </div>
                      </div>

                      <div className="border rounded-lg p-3 text-xs space-y-1" data-testid="grv-history-audit-formulas">
                        <div className="flex justify-between" data-testid="grv-history-audit-subtotal-formula">
                          <span className="text-gray-600">Subtotal formula:</span>
                          <span className="font-mono">
                            {formatCurrency(historyCalculationAudit.itemsSubtotalExclVat)} {includeDeliveryInTotalsForDetail ? "+ " + formatCurrency(historyCalculationAudit.deliveryFeeExclVat) : "+ R 0.00"}
                          </span>
                        </div>
                        <div className="flex justify-between" data-testid="grv-history-audit-vat-formula">
                          <span className="text-gray-600">VAT formula:</span>
                          <span className="font-mono">
                            {formatCurrency(historyCalculationAudit.itemsVat)} {includeDeliveryInTotalsForDetail ? "+ " + formatCurrency(historyCalculationAudit.deliveryFeeVat) : "+ R 0.00"}
                          </span>
                        </div>
                        <div className="flex justify-between" data-testid="grv-history-audit-total-formula">
                          <span className="text-gray-600">Total formula:</span>
                          <span className="font-mono">
                            {formatCurrency(historyCalculationAudit.subtotalExclVat)} + {formatCurrency(historyCalculationAudit.vatTotal)} = {formatCurrency(historyCalculationAudit.totalInclVat)}
                          </span>
                        </div>
                        <div className="flex justify-between border-t pt-1" data-testid="grv-history-audit-variance-row">
                          <span className="font-medium">Variance ({reconciliation?.comparison_basis === "total_incl_vat" ? "Total" : "Subtotal"} vs Invoice):</span>
                          <span className={`font-semibold ${historyCalculationAudit.isBalanced ? "text-emerald-700" : "text-red-700"}`} data-testid="grv-history-audit-variance-value">
                            {formatCurrency(historyCalculationAudit.variance)}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className={`rounded border p-2 ${historyCalculationAudit.subtotalCheckPass ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`} data-testid="grv-history-audit-subtotal-check">
                          Subtotal check: {historyCalculationAudit.subtotalCheckPass ? "PASS" : "FAIL"}
                        </div>
                        <div className={`rounded border p-2 ${historyCalculationAudit.vatCheckPass ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`} data-testid="grv-history-audit-vat-check">
                          VAT check: {historyCalculationAudit.vatCheckPass ? "PASS" : "FAIL"}
                        </div>
                        <div className={`rounded border p-2 ${historyCalculationAudit.totalCheckPass ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`} data-testid="grv-history-audit-total-check">
                          Total check: {historyCalculationAudit.totalCheckPass ? "PASS" : "FAIL"}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Notes */}
                {selectedGrv.notes && (
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-xs text-gray-500 uppercase mb-1">Notes</div>
                      <div className="text-sm">{selectedGrv.notes}</div>
                    </CardContent>
                  </Card>
                )}

                {/* Claims Section */}
                <Card className="border-orange-200" data-testid="grv-claims-section">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <ShieldAlert className="h-4 w-4 text-orange-600" />
                        <span className="text-sm font-semibold text-orange-900">Claims</span>
                        {claims.length > 0 && (
                          <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full">{claims.length}</span>
                        )}
                      </div>
                      <Button size="sm" variant="outline" onClick={openClaimForm} data-testid="create-claim-btn">
                        <Plus className="w-3 h-3 mr-1" /> Create Claim
                      </Button>
                    </div>

                    {/* Claim Creation Form */}
                    {showClaimForm && (
                      <div className="border rounded-lg p-3 mb-3 bg-orange-50/50 space-y-3" data-testid="claim-form">
                        <div className="text-sm font-medium">Select items to claim:</div>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {claimItems.map((item, idx) => (
                            <div key={idx} className={`border rounded p-2 text-sm ${item.selected ? "bg-white border-orange-300" : "bg-gray-50"}`}>
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={item.selected}
                                  onChange={(e) => {
                                    const updated = [...claimItems];
                                    updated[idx].selected = e.target.checked;
                                    setClaimItems(updated);
                                  }}
                                  className="rounded"
                                />
                                <span className="font-medium flex-1">{item.product_name}</span>
                                <span className="text-xs text-gray-500">PLU: {item.plu_code} | Received: {item.quantity_received}</span>
                              </div>
                              {item.selected && (
                                <div className="grid grid-cols-3 gap-2 mt-2 ml-6">
                                  <div>
                                    <label className="text-xs text-gray-500">Type</label>
                                    <Select value={item.claim_type} onValueChange={(val) => {
                                      const updated = [...claimItems];
                                      updated[idx].claim_type = val;
                                      setClaimItems(updated);
                                    }}>
                                      <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="DAMAGED">Damaged</SelectItem>
                                        <SelectItem value="SHORT_DELIVERED">Short Delivered</SelectItem>
                                        <SelectItem value="EXPIRED">Expired</SelectItem>
                                        <SelectItem value="WRONG_ITEM">Wrong Item</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Qty</label>
                                    <Input
                                      type="number"
                                      min="1"
                                      max={item.quantity_received}
                                      value={item.claim_qty || ""}
                                      onChange={(e) => {
                                        const updated = [...claimItems];
                                        updated[idx].claim_qty = parseFloat(e.target.value) || 0;
                                        setClaimItems(updated);
                                      }}
                                      className="h-7 text-xs"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Reason</label>
                                    <Input
                                      value={item.reason}
                                      onChange={(e) => {
                                        const updated = [...claimItems];
                                        updated[idx].reason = e.target.value;
                                        setClaimItems(updated);
                                      }}
                                      className="h-7 text-xs"
                                      placeholder="Optional note"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">General Notes</label>
                          <Input
                            value={claimNotes}
                            onChange={(e) => setClaimNotes(e.target.value)}
                            placeholder="Additional notes for this claim..."
                            className="text-sm"
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => setShowClaimForm(false)}>Cancel</Button>
                          <Button size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={handleSaveClaim} disabled={savingClaim} data-testid="save-claim-btn">
                            {savingClaim ? "Saving..." : "Create Claim"}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Claims List */}
                    {claimsLoading ? (
                      <div className="text-sm text-gray-500">Loading claims...</div>
                    ) : claims.length === 0 && !showClaimForm ? (
                      <div className="text-sm text-gray-400 text-center py-3">No claims against this GRV</div>
                    ) : (
                      <div className="space-y-2">
                        {claims.map((claim) => {
                          const isExpanded = expandedClaim === claim.id;
                          const statusColors = {
                            DRAFT: "bg-gray-100 text-gray-700",
                            SUBMITTED: "bg-blue-100 text-blue-700",
                            APPROVED: "bg-green-100 text-green-700",
                            CREDIT_RECEIVED: "bg-emerald-100 text-emerald-700",
                            CANCELLED: "bg-red-100 text-red-700",
                          };
                          return (
                            <div key={claim.id} className="border rounded-lg overflow-hidden" data-testid={`claim-${claim.claim_number}`}>
                              <div
                                className="flex items-center gap-2 p-2 cursor-pointer hover:bg-gray-50 text-sm"
                                onClick={() => setExpandedClaim(isExpanded ? null : claim.id)}
                              >
                                {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                <span className="font-mono font-semibold text-orange-700">{claim.claim_number}</span>
                                <span className={`px-1.5 py-0.5 text-xs rounded-full ${statusColors[claim.status] || "bg-gray-100"}`}>
                                  {claim.status.replace("_", " ")}
                                </span>
                                <span className="flex-1" />
                                <span className="font-semibold text-red-600">{formatCurrency(claim.total_claim_value)}</span>
                                <span className="text-xs text-gray-400">{claim.created_at?.split("T")[0]}</span>
                              </div>
                              {isExpanded && (
                                <div className="border-t p-3 bg-gray-50 space-y-3">
                                  {/* Claim items */}
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="text-gray-500 uppercase">
                                        <th className="text-left py-1">Product</th>
                                        <th className="text-left py-1">Type</th>
                                        <th className="text-right py-1">Qty</th>
                                        <th className="text-right py-1">Unit Cost</th>
                                        <th className="text-right py-1">Value</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {claim.items.map((item, i) => (
                                        <tr key={i} className="border-t">
                                          <td className="py-1">{item.product_name} <span className="text-gray-400">({item.plu_code})</span></td>
                                          <td className="py-1">{item.claim_type.replace("_", " ")}</td>
                                          <td className="py-1 text-right">{item.quantity}</td>
                                          <td className="py-1 text-right">{formatCurrency(item.unit_cost)}</td>
                                          <td className="py-1 text-right font-semibold text-red-600">{formatCurrency(item.claim_value)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  {claim.notes && <div className="text-xs bg-yellow-50 border border-yellow-200 rounded p-2"><strong>Notes:</strong> {claim.notes}</div>}

                                  {/* Photos */}
                                  <div>
                                    <div className="flex items-center gap-2 mb-1">
                                      <Camera className="w-3 h-3 text-gray-500" />
                                      <span className="text-xs font-medium text-gray-600">Photos ({(claim.photos || []).length})</span>
                                      <label className="cursor-pointer text-xs text-blue-600 hover:underline">
                                        {uploadingPhoto === claim.id ? "Uploading..." : "+ Add Photo"}
                                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(claim.id, e)} disabled={uploadingPhoto === claim.id} />
                                      </label>
                                    </div>
                                    {(claim.photos || []).length > 0 && (
                                      <div className="flex flex-wrap gap-2">
                                        {claim.photos.map((photo, pi) => (
                                          <img key={pi} src={photo.url} alt={`Claim photo ${pi+1}`} className="w-20 h-20 object-cover rounded border" />
                                        ))}
                                      </div>
                                    )}
                                  </div>

                                  {/* Actions */}
                                  <div className="flex items-center gap-2 pt-2 border-t">
                                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handlePrintClaim(claim)}>
                                      <Printer className="w-3 h-3 mr-1" /> Print
                                    </Button>
                                    <Button size="sm" variant="outline" className="h-7 text-xs text-blue-600 border-blue-200" onClick={() => handleEmailClaim(claim)} data-testid={`email-claim-${claim.id}`}>
                                      <Mail className="w-3 h-3 mr-1" /> Email to Supplier
                                    </Button>
                                    {claim.status === "DRAFT" && (
                                      <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700" onClick={() => updateClaimStatus(claim.id, "SUBMITTED")}>
                                        <Send className="w-3 h-3 mr-1" /> Submit to Supplier
                                      </Button>
                                    )}
                                    {claim.status === "SUBMITTED" && (
                                      <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700" onClick={() => updateClaimStatus(claim.id, "APPROVED")}>
                                        <CheckCircle className="w-3 h-3 mr-1" /> Mark Approved
                                      </Button>
                                    )}
                                    {claim.status === "APPROVED" && (
                                      <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => updateClaimStatus(claim.id, "CREDIT_RECEIVED")}>
                                        <CheckCircle className="w-3 h-3 mr-1" /> Credit Received
                                      </Button>
                                    )}
                                    {claim.status !== "CANCELLED" && claim.status !== "CREDIT_RECEIVED" && (
                                      <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200" onClick={() => updateClaimStatus(claim.id, "CANCELLED")}>
                                        <XCircle className="w-3 h-3 mr-1" /> Cancel
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        <div className="border-t pt-4 flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Delete Confirmation Dialog - Outside main Dialog to prevent interference */}
    <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Delete & Reverse GRV?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                You are about to <strong>permanently delete</strong> GRV <strong className="text-blue-600">{grvToDelete?.grv_number}</strong>.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                <p className="font-semibold text-red-800 mb-2">This action will:</p>
                <ul className="list-disc list-inside space-y-1 text-red-700">
                  <li>Subtract all received quantities from product stock</li>
                  <li>Reverse the cost price calculations</li>
                  <li>Delete any expiry batches created by this GRV</li>
                  <li>Remove the supplier ledger entry</li>
                </ul>
              </div>
              <p className="text-gray-600">
                <strong>Supplier:</strong> {grvToDelete?.supplier_name}<br />
                <strong>Invoice:</strong> {grvToDelete?.supplier_invoice_number || "N/A"}<br />
                <strong>Items:</strong> {grvToDelete?.items?.length || 0}<br />
                <strong>Total:</strong> {formatCurrency(grvToDelete?.total)}
              </p>
              <p className="text-red-600 font-medium">
                ⚠️ This action cannot be undone!
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirmDelete}
            disabled={deleting}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {deleting ? "Deleting..." : "Yes, Delete & Reverse"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <ShelfTalkerModal
      open={showShelfTalker}
      onClose={() => setShowShelfTalker(false)}
      items={shelfTalkerItems}
    />
  </>
  );
};

export default GRVHistoryModal;
